import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@via/hooks/useAuth.tsx";
import AuthenticatorSetup from "@via/pages/account/profile/security/authenticator-setup.tsx";
import CodeConfirm from "@via/pages/account/profile/security/code-confirm.tsx";
import {
  useDisableMfa,
  useForgetDevices,
  useMfaStatus,
  useRegenerateRecoveryCodes,
  useSignOutEverywhere,
} from "@via/pages/authentication/mfa/mfa.api.tsx";
import { goTo } from "@via/api/mfa-destination";
import RecoveryCodesPanel from "@via/pages/authentication/mfa/recovery-codes-panel.tsx";
import { ROUTES } from "@via/utils/constants.tsx";
import { normalizeRedirectPath } from "@via/utils/post-login-redirect";
import type { MfaRecoveryCodesResponse } from "common";
import { type JSX, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

type Panel = "none" | "setup" | "replace" | "regenerate" | "disable";

const WRAPPER =
  "max-w-2xl space-y-6 p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg";
const BUTTON =
  "rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50";

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

export default function Security(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setUser } = useAuth();
  const codesPending = searchParams.get("codes") === "pending";
  const continueTo = normalizeRedirectPath(searchParams.get("continue"));
  const status = useMfaStatus();
  const regenerate = useRegenerateRecoveryCodes();
  const disable = useDisableMfa();
  const forget = useForgetDevices();
  const signOut = useSignOutEverywhere();
  const [panel, setPanel] = useState<Panel>("none");
  const [codes, setCodes] = useState<string[] | null>(null);
  const leave = () => {
    if (continueTo) goTo(continueTo, navigate);
  };

  if (status.isPending) {
    return (
      <div className={WRAPPER}>
        <p className="text-sm text-gray-600">Loading</p>
      </div>
    );
  }
  if (!status.data) {
    return (
      <div className={WRAPPER}>
        <h2 className="via-h2">Multi-factor sign-in</h2>
        <p className="text-sm text-gray-600">
          {status.error?.response?.status === 404
            ? "Multi-factor sign-in is not turned on for this installation."
            : "Multi-factor sign-in settings could not be loaded."}
        </p>
      </div>
    );
  }
  const s = status.data;
  const showReminder = codesPending && s.enrolled && !s.codesAcknowledged;

  if (codes) {
    return (
      <div className={WRAPPER}>
        <RecoveryCodesPanel
          codes={codes}
          doneLabel={continueTo ? "Continue" : "Done"}
          onDone={() => {
            setCodes(null);
            leave();
          }}
        />
      </div>
    );
  }

  return (
    <section className={WRAPPER}>
      <header className="via-page-heading-wrapper">
        <h2 className="via-h2">Multi-factor sign-in</h2>
        <p className="mt-1 text-sm text-gray-600">
          {s.enrolled
            ? `On since ${s.activatedAt ? new Date(s.activatedAt).toLocaleDateString() : "setup"}. Sign-in asks for a code from your authenticator app.`
            : "Off for your account."}
        </p>
      </header>

      {s.handoffPending && (
        <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
          An administrator reset multi-factor sign-in for your account. Sign
          out, then sign in again with the one-time code they gave you.
        </p>
      )}

      {showReminder && (
        <div
          data-testid="mfa-codes-reminder"
          className="space-y-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
          <p>
            You have not confirmed saving your recovery codes. You have{" "}
            {plural(
              s.recoveryCodesRemaining,
              "recovery code",
              "recovery codes"
            )}{" "}
            left. Create a new set below and keep it somewhere safe.
          </p>
          {continueTo && (
            <button type="button" className={BUTTON} onClick={leave}>
              Continue without new codes
            </button>
          )}
        </div>
      )}
      {/* Whatever the two status sources say, a user sent here with a destination can leave. */}
      {!showReminder && continueTo && (
        <button type="button" className={BUTTON} onClick={leave}>
          Continue
        </button>
      )}

      {!s.enrolled && s.mode === "optional" && !s.handoffPending && (
        <button
          type="button"
          className={BUTTON}
          onClick={() => setPanel("setup")}>
          Set up authenticator
        </button>
      )}

      {s.enrolled && (
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            Recovery codes left: {s.recoveryCodesRemaining}. Remembered
            browsers: {s.rememberedDevices}.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={BUTTON}
              onClick={() => setPanel("replace")}>
              Replace authenticator
            </button>
            <button
              type="button"
              className={BUTTON}
              onClick={() => setPanel("regenerate")}>
              Create new recovery codes
            </button>
            <button
              type="button"
              className={BUTTON}
              disabled={s.rememberedDevices === 0 || forget.isPending}
              onClick={() => forget.mutate()}>
              Forget remembered browsers
            </button>
            {s.canDisable && (
              <button
                type="button"
                className={BUTTON}
                onClick={() => setPanel("disable")}>
                Turn off
              </button>
            )}
          </div>
        </div>
      )}

      {(panel === "setup" || panel === "replace") && (
        <AuthenticatorSetup
          needsStepUp={panel === "replace"}
          onDone={newCodes => {
            setPanel("none");
            setCodes(newCodes);
          }}
          onCancel={() => setPanel("none")}
        />
      )}
      {panel === "regenerate" && (
        <CodeConfirm<MfaRecoveryCodesResponse>
          title="Create new recovery codes"
          action={code => regenerate.mutateAsync({ code })}
          onDone={result => {
            setPanel("none");
            setCodes(result.recoveryCodes);
          }}
          onCancel={() => setPanel("none")}
        />
      )}
      {panel === "disable" && (
        <CodeConfirm
          title="Turn off multi-factor sign-in"
          action={code => disable.mutateAsync({ code })}
          onDone={() => setPanel("none")}
          onCancel={() => setPanel("none")}
        />
      )}

      <div className="border-t border-gray-200 pt-6">
        <p className="text-sm text-gray-600">
          Signs you out in every browser, including this one.
        </p>
        <button
          type="button"
          className={`${BUTTON} mt-2`}
          disabled={signOut.isPending}
          onClick={() =>
            signOut.mutate(undefined, {
              onSuccess: () => {
                queryClient.clear();
                setUser(false);
                navigate(ROUTES.LOGIN);
              },
            })
          }>
          Sign out everywhere
        </button>
        {signOut.isError && (
          <p role="alert" className="mt-2 text-sm font-semibold text-rose-600">
            Something went wrong. Please try again.
          </p>
        )}
      </div>
    </section>
  );
}
